import {
	Clock,
	Effect,
	Equal,
	Option,
	Result,
	Schema as S,
	Schedule,
	Stream,
} from "effect";
import type { Runtime } from "foldkit";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import * as Dom from "foldkit/dom";
import * as Navigation from "foldkit/navigation";
import { evo } from "foldkit/struct";
import * as Subscription from "foldkit/subscription";
import * as Update from "foldkit/update";
import * as Url from "foldkit/url";

import { settle, start } from "../api/asyncData";
import { getFeaturesConfig } from "../api/commands/account";
import { getAttributeSchema } from "../api/commands/attributeSchema";
import { getEventActivity } from "../api/commands/events";
import { getLedgerBalance } from "../api/commands/ledger";
import { getTags } from "../api/commands/tags";
import type { ClerkAdapter } from "../auth/adapter";
import { Auth } from "../auth/model";
import { initTranslations } from "../i18n";
import { PageMessage, PageModel, type PageTag, pages } from "../pages";
import { Message as AiMessage } from "../pages/ai/message";
import { session as aiSession } from "../pages/ai/session";
import {
	countdownDependencies as aiCountdown,
	sessionDependencies as aiSessionDependencies,
} from "../pages/ai/update";
import { Message as ChatMessage } from "../pages/chat/message";
import { streamDependencies as chatDependencies } from "../pages/chat/update";
import { Message as RecordMessage } from "../pages/record/message";
import { Message as SearchedMessage } from "../pages/searched/message";
import {
	SEARCH_INPUT_ID,
	searchDebounceDependencies as searchedDependencies,
} from "../pages/searched/update";
import type { Api, Page, PageReturn, Shared } from "../pages/shared";
import { localIsoDate } from "../widgets/addEntry";
import {
	type Message as AiChatMessage,
	PendingRequest,
} from "../widgets/aiChat";
import { streamChat } from "../widgets/aiChatStream";
import * as LogInteraction from "../widgets/logInteraction";
import * as Sidebar from "../widgets/sidebar";
import { type Handles, identify } from "./analytics";
import {
	readIntegrationsEnabled,
	writeIntegrationsEnabled,
} from "./developerOptions";
import { Message } from "./message";
import {
	type Model,
	a2aContextEnabled,
	activePageTag,
	toShared,
} from "./model";
import {
	readImportDismissed,
	shouldOfferImport,
	writeImportDismissed,
} from "./onboarding";
import {
	Route,
	asOfOf,
	linkHref,
	toHref,
	urlToRoute,
	withAsOf,
} from "./routes";
import {
	Theme,
	applyTheme,
	readStoredTheme,
	systemTheme,
	toggle,
} from "./theme";

type Return = Update.Return<Model, Message, Api>;
type Step = Update.Step<Model, Message, Api>;

// COMMAND

export const Navigate = Command.define("Navigate", {
	args: { url: S.String },
	messages: [Message.CompletedNavigate],
	execute: ({ url }) =>
		Navigation.pushUrl(url).pipe(Effect.as(Message.CompletedNavigate())),
});

export const LoadExternal = Command.define("LoadExternal", {
	args: { href: S.String },
	messages: [Message.CompletedNavigate],
	execute: ({ href }) =>
		Navigation.load(href).pipe(Effect.as(Message.CompletedNavigate())),
});

export const FetchBalance = getLedgerBalance.command(
	Message.CompletedFetchBalance,
	({ generation, result }) =>
		Message.CompletedFetchBalance({ generation, result }),
);

export const FetchActivity = getEventActivity.command(
	Message.CompletedFetchActivity,
	({ generation, month, result }) =>
		Message.CompletedFetchActivity({ generation, month, result }),
);

/** Prefetch at sign-in: the month the picker will open on, read from the Clock at issue time. */
export const FetchCurrentActivity = Command.define("FetchCurrentActivity", {
	args: { generation: S.Number },
	messages: [Message.CompletedFetchActivity],
	execute: ({ generation }) =>
		Clock.currentTimeMillis.pipe(
			Effect.map((millis) => localIsoDate(new Date(millis)).slice(0, 7)),
			Effect.flatMap((month) =>
				getEventActivity
					.run({ month })
					.pipe(
						Effect.map((result) =>
							Message.CompletedFetchActivity({ generation, month, result }),
						),
					),
			),
		),
});

/** The picker opens on the travelled month when the URL carries one, else on today's. */
const prefetchActivity = (model: Model, generation: number) =>
	Option.match(asOfOf(model.route), {
		onNone: () => FetchCurrentActivity({ generation }),
		onSome: (asOf) => FetchActivity({ generation, month: asOf.slice(0, 7) }),
	});

/** Show `month` in the picker and (re)load its heatmap; a month change drops the old shading. */
const showActivityMonth = (model: Model, month: string) => ({
	model: evo(model, {
		activityMonth: () => month,
		activity: (activity) =>
			month === model.activityMonth ? start(activity) : start(AsyncData.Idle()),
	}),
	commands: [FetchActivity({ generation: model.sessionGeneration, month })],
});

export const FetchFeatures = getFeaturesConfig.command(
	Message.CompletedFetchFeatures,
	({ generation, result }) =>
		Message.CompletedFetchFeatures({ generation, result }),
);

/** Tag suggestions for the Entry combobox (KTD3): once on sign-in, again on `TagsChanged`. */
export const FetchTags = getTags.command(
	Message.CompletedFetchTags,
	({ generation, result }) =>
		Message.CompletedFetchTags({ generation, result }),
);

export const FetchAttributeSchema = getAttributeSchema.command(
	Message.CompletedFetchAttributeSchema,
	({ generation, result }) =>
		Message.CompletedFetchAttributeSchema({ generation, result }),
);

export const ApplyTheme = Command.define("ApplyTheme", {
	args: { theme: Theme },
	messages: [Message.CompletedApplyTheme],
	execute: ({ theme }) =>
		Effect.sync(() => applyTheme(theme)).pipe(
			Effect.as(Message.CompletedApplyTheme()),
		),
});

export const InitTranslations = Command.define("InitTranslations", {
	messages: [Message.CompletedInitTranslations],
	execute: Effect.promise(initTranslations).pipe(
		Effect.map((ready) => Message.CompletedInitTranslations({ ready })),
	),
});

export const PersistIntegrations = Command.define("PersistIntegrations", {
	args: { enabled: S.Boolean },
	messages: [Message.CompletedPersistIntegrations],
	execute: ({ enabled }) =>
		Effect.sync(() => writeIntegrationsEnabled(enabled)).pipe(
			Effect.as(Message.CompletedPersistIntegrations()),
		),
});

/** Cmd-K / Ctrl-K focus: the page's own search (first search input in the document); a missing input (page still rendering) is not an error. */
export const FocusSearch = Command.define("FocusSearch", {
	messages: [Message.CompletedFocusSearch],
	execute: Dom.focus(`#${SEARCH_INPUT_ID}, input[type="search"]`).pipe(
		Effect.as(Message.CompletedFocusSearch()),
		Effect.catch(() => Effect.succeed(Message.CompletedFocusSearch())),
	),
});

/** Where the page has no search of its own: open Records and focus its search once it renders. */
export const OpenSearch = Command.define("OpenSearch", {
	args: { url: S.String },
	messages: [Message.CompletedFocusSearch],
	execute: ({ url }) =>
		Navigation.pushUrl(url).pipe(
			Effect.andThen(
				Dom.focus(`#${SEARCH_INPUT_ID}`).pipe(
					// ponytail: polls for the Records page to render; 20 x 50ms then gives up quietly.
					Effect.retry({ times: 20, schedule: Schedule.spaced("50 millis") }),
				),
			),
			Effect.as(Message.CompletedFocusSearch()),
			Effect.catch(() => Effect.succeed(Message.CompletedFocusSearch())),
		),
});

export const PersistImportDismissed = Command.define("PersistImportDismissed", {
	args: {},
	messages: [Message.CompletedPersistImportDismissed],
	execute: () =>
		Effect.sync(writeImportDismissed).pipe(
			Effect.as(Message.CompletedPersistImportDismissed()),
		),
});

/** Built per boot so the analytics handles never live at module scope. */
export const makeIdentifyAnalytics = (handles: Handles) =>
	Command.define("IdentifyAnalytics", {
		args: { id: S.String, email: S.String, createdAt: S.NullOr(S.String) },
		messages: [Message.CompletedIdentifyAnalytics],
		execute: (user) =>
			Effect.sync(() => identify(handles, user)).pipe(
				Effect.as(Message.CompletedIdentifyAnalytics()),
				Effect.catch(() =>
					Effect.succeed(Message.CompletedIdentifyAnalytics()),
				),
			),
	});
export type IdentifyAnalytics = ReturnType<typeof makeIdentifyAnalytics>;

/** v1 Settings: `signOut({ redirectUrl: "https://rhysomn.com" })` after the account is deleted. */
export const makeSignOut = (adapter: Pick<ClerkAdapter, "signOut">) =>
	Command.define("SignOut", {
		args: { redirectUrl: S.String },
		messages: [Message.CompletedSignOut],
		execute: ({ redirectUrl }) =>
			Effect.promise(() => adapter.signOut(redirectUrl)).pipe(
				Effect.as(Message.CompletedSignOut()),
				Effect.catch(() => Effect.succeed(Message.CompletedSignOut())),
			),
	});
export type SignOut = ReturnType<typeof makeSignOut>;

// PAGE SLOTS

type PageInput<Msg> = Readonly<{ message: Msg; shared: Shared }>;

/**
 * Everything the root needs to drive one page Submodel (KTD2): enter on a
 * transition, forward a within-route change, and fold its Messages. The
 * child slice is only ever written through the child's own functions.
 */
const slot = <M, Msg, Out = never>(
	tag: PageTag,
	page: Page<M, Msg, Out>,
	wrapModel: (fields: { model: M }) => PageModel,
	wrapMessage: (fields: { message: Msg }) => PageMessage,
	foldOutMessage: (outMessage: Out) => Step = () => (model) => ({ model }),
) => {
	const toParentMessage = (message: Msg): Message =>
		Message.GotPageMessage({ message: wrapMessage({ message }) });
	const read = (model: Model): Option.Option<M> =>
		Option.flatMap(model.page, (active) =>
			// The slot tag identifies the page module, so this is the module's own Model.
			active._tag === tag ? Option.some(active.model as M) : Option.none(),
		);
	const write = (model: Model, next: M): Model =>
		evo(model, { page: () => Option.some(wrapModel({ model: next })) });
	const lift = (model: Model, result: PageReturn<M, Msg, Out>): Return => {
		const lifted: Return = {
			model: write(model, result.model),
			commands: Command.mapMessages(result.commands, toParentMessage),
		};
		// `init` may report a fact to the root too.
		return result.outMessage === undefined
			? lifted
			: Update.combine(lifted.model, [
					() => lifted,
					foldOutMessage(result.outMessage),
				]);
	};
	return {
		enter: (model: Model, shared: Shared): Return =>
			lift(model, page.init(model.route, shared)),
		changedRoute: (model: Model, previous: Route, shared: Shared): Return =>
			Option.match(read(model), {
				onNone: () => ({ model }),
				onSome: (child) =>
					lift(model, page.changedRoute(child, previous, model.route, shared)),
			}),
		fold: Update.foldChild({
			update: (child: M, input: PageInput<Msg>) =>
				page.update(child, input.message, input.shared),
			read,
			write,
			toParentMessage,
			foldOutMessage,
		}),
	};
};

// PAGE FACTS

/** The Tags page changed tag data: refresh the suggestions the Entry combobox reads. */
const foldTagsChanged: Step = (model) =>
	model.auth._tag === "SignedIn"
		? {
				model: evo(model, { tags: start }),
				commands: [FetchTags({ generation: model.sessionGeneration })],
			}
		: { model };

const foldSystemTheme: Step = (model) => {
	const theme = systemTheme();
	return {
		model: evo(model, { theme: () => theme }),
		commands: [ApplyTheme({ theme })],
	};
};

/** The flag flips on the root and persists (KTD3); the Developer page reports it as an OutMessage. */
const setIntegrations =
	(enabled: boolean): Step =>
	(model) => ({
		model: evo(model, { integrationsEnabled: () => enabled }),
		commands: [PersistIntegrations({ enabled })],
	});

/**
 * First run (R20, KTD7): the records list resolved to nothing, unfiltered and
 * not time-travelling, so offer the import once. Offering is what dismisses it
 * -- Skip is a plain link, and a reload should not put the user back here.
 */
const offerImport: Step = (model) => {
	const offer =
		!model.importDismissed &&
		Option.isNone(asOfOf(model.route)) &&
		Option.exists(
			model.page,
			(page) =>
				(page._tag === "Home" || page._tag === "Records") &&
				page.model.searchText.trim() === "" &&
				shouldOfferImport(page.model, model.importDismissed),
		);
	if (!offer) return { model };
	return {
		model: evo(model, { importDismissed: () => true }),
		commands: [
			PersistImportDismissed({}),
			...navigateTo(
				Route.Import({ asOf: Option.none(), onboarding: Option.some("1") }),
			),
		],
	};
};

/**
 * The metric of a `LimitExceeded` result anywhere in a (nested) child Message.
 * Every API result Message carries `result` (KTD5) and every wrapper `message`,
 * so the root sees a plan-limit hit without each page reporting it.
 */
export const limitHitIn = (value: unknown): Option.Option<string> => {
	if (typeof value !== "object" || value === null) return Option.none();
	const { result, message } = value as { result?: unknown; message?: unknown };
	if (Result.isResult(result) && Result.isFailure(result)) {
		const error = result.failure as { _tag?: unknown; metric?: unknown };
		return error._tag === "LimitExceeded" && typeof error.metric === "string"
			? Option.some(error.metric)
			: Option.none();
	}
	return limitHitIn(message);
};

/** Records, entries and file uploads grow with a subscription, so they open the notice. */
const noteLimitHit =
	(message: unknown): Step =>
	(model) =>
		Option.exists(
			limitHitIn(message),
			(metric) =>
				metric === "records" ||
				metric === "entries" ||
				metric === "storage_bytes",
		)
			? { model: evo(model, { limitNoticeOpen: () => true }) }
			: { model };

const makeSlots = (SignOut: SignOut) => {
	const foldAccountDeleted: Step = (model) => ({
		model: signOut(model),
		commands: [SignOut({ redirectUrl: "https://rhysomn.com" })],
	});
	return {
		Home: slot("Home", pages.Home, PageModel.Home, PageMessage.Home),
		Records: slot(
			"Records",
			pages.Records,
			PageModel.Records,
			PageMessage.Records,
		),
		Record: slot("Record", pages.Record, PageModel.Record, PageMessage.Record),
		Entry: slot("Entry", pages.Entry, PageModel.Entry, PageMessage.Entry),
		Settings: slot(
			"Settings",
			pages.Settings,
			PageModel.Settings,
			PageMessage.Settings,
			(outMessage) =>
				pages.Settings.OutMessage.match(outMessage, {
					RequestedThemeToggle: () => foldThemeToggle,
					RequestedSystemTheme: () => foldSystemTheme,
					AccountDeleted: () => foldAccountDeleted,
				}),
		),
		Credits: slot(
			"Credits",
			pages.Credits,
			PageModel.Credits,
			PageMessage.Credits,
		),
		Rates: slot("Rates", pages.Rates, PageModel.Rates, PageMessage.Rates),
		Developer: slot(
			"Developer",
			pages.Developer,
			PageModel.Developer,
			PageMessage.Developer,
			(outMessage) =>
				pages.Developer.OutMessage.match(outMessage, {
					IntegrationsToggled: ({ enabled }) => setIntegrations(enabled),
				}),
		),
		Developers: slot(
			"Developers",
			pages.Developers,
			PageModel.Developers,
			PageMessage.Developers,
		),
		Admin: slot("Admin", pages.Admin, PageModel.Admin, PageMessage.Admin),
		Audit: slot("Audit", pages.Audit, PageModel.Audit, PageMessage.Audit),
		AI: slot("AI", pages.AI, PageModel.AI, PageMessage.AI),
		Chat: slot("Chat", pages.Chat, PageModel.Chat, PageMessage.Chat),
		Integrations: slot(
			"Integrations",
			pages.Integrations,
			PageModel.Integrations,
			PageMessage.Integrations,
		),
		KeepInTouch: slot(
			"KeepInTouch",
			pages.KeepInTouch,
			PageModel.KeepInTouch,
			PageMessage.KeepInTouch,
		),
		Tags: slot(
			"Tags",
			pages.Tags,
			PageModel.Tags,
			PageMessage.Tags,
			() => foldTagsChanged,
		),
		AttributeSchema: slot(
			"AttributeSchema",
			pages.AttributeSchema,
			PageModel.AttributeSchema,
			PageMessage.AttributeSchema,
		),
		Import: slot("Import", pages.Import, PageModel.Import, PageMessage.Import),
		Capture: slot(
			"Capture",
			pages.Capture,
			PageModel.Capture,
			PageMessage.Capture,
		),
		Tasks: slot("Tasks", pages.Tasks, PageModel.Tasks, PageMessage.Tasks),
		Task: slot("Task", pages.Task, PageModel.Task, PageMessage.Task),
		Knowledge: slot(
			"Knowledge",
			pages.Knowledge,
			PageModel.Knowledge,
			PageMessage.Knowledge,
		),
		Note: slot("Note", pages.Note, PageModel.Note, PageMessage.Note),
		Agents: slot("Agents", pages.Agents, PageModel.Agents, PageMessage.Agents),
		NotFound: slot(
			"NotFound",
			pages.NotFound,
			PageModel.NotFound,
			PageMessage.NotFound,
		),
	};
};
type Slots = ReturnType<typeof makeSlots>;

/**
 * Reconciles the active page with auth, route, admin, and flag state
 * (KTD7, KTD8). Entering runs the page's `init`; leaving drops the slice, so
 * a late result Message finds no child and is a no-op. `maybePreviousRoute`
 * is `Some` only from `ChangedUrl`, which is the only source of a `stayed`
 * transition.
 */
const syncPageWith =
	(slots: Slots, maybePreviousRoute: Option.Option<Route>): Step =>
	(model) => {
		const maybeShared = toShared(model);
		const maybeTag = activePageTag(model);
		if (Option.isNone(maybeTag) || Option.isNone(maybeShared)) {
			return { model: evo(model, { page: () => Option.none() }) };
		}
		const active = slots[maybeTag.value];
		const shared = maybeShared.value;
		const sameTag = Option.exists(
			model.page,
			(page) => page._tag === maybeTag.value,
		);
		if (!sameTag) {
			return active.enter(model, shared);
		}
		return Option.match(maybePreviousRoute, {
			// Shared may have changed under a page that stayed (U12 record tab counts).
			onNone: () =>
				maybeTag.value === "Record"
					? slots.Record.fold(model, {
							message: RecordMessage.ChangedShared(),
							shared,
						})
					: { model },
			onSome: (previous) =>
				Equal.equals(previous, model.route)
					? { model }
					: active.changedRoute(model, previous, shared),
		});
	};

const foldPageMessage = (
	slots: Slots,
	model: Model,
	message: PageMessage,
	shared: Shared,
): Return =>
	PageMessage.match(message, {
		Home: ({ message }) => slots.Home.fold(model, { message, shared }),
		Records: ({ message }) => slots.Records.fold(model, { message, shared }),
		Record: ({ message }) => slots.Record.fold(model, { message, shared }),
		Entry: ({ message }) => slots.Entry.fold(model, { message, shared }),
		Settings: ({ message }) => slots.Settings.fold(model, { message, shared }),
		Credits: ({ message }) => slots.Credits.fold(model, { message, shared }),
		Rates: ({ message }) => slots.Rates.fold(model, { message, shared }),
		Developer: ({ message }) =>
			slots.Developer.fold(model, { message, shared }),
		Developers: ({ message }) =>
			slots.Developers.fold(model, { message, shared }),
		Admin: ({ message }) => slots.Admin.fold(model, { message, shared }),
		Audit: ({ message }) => slots.Audit.fold(model, { message, shared }),
		AI: ({ message }) => slots.AI.fold(model, { message, shared }),
		Chat: ({ message }) => slots.Chat.fold(model, { message, shared }),
		Integrations: ({ message }) =>
			slots.Integrations.fold(model, { message, shared }),
		Tags: ({ message }) => slots.Tags.fold(model, { message, shared }),
		KeepInTouch: ({ message }) =>
			slots.KeepInTouch.fold(model, { message, shared }),
		AttributeSchema: ({ message }) =>
			slots.AttributeSchema.fold(model, { message, shared }),
		Import: ({ message }) => slots.Import.fold(model, { message, shared }),
		Capture: ({ message }) => slots.Capture.fold(model, { message, shared }),
		Tasks: ({ message }) => slots.Tasks.fold(model, { message, shared }),
		Task: ({ message }) => slots.Task.fold(model, { message, shared }),
		Knowledge: ({ message }) =>
			slots.Knowledge.fold(model, { message, shared }),
		Note: ({ message }) => slots.Note.fold(model, { message, shared }),
		Agents: ({ message }) => slots.Agents.fold(model, { message, shared }),
		NotFound: ({ message }) => slots.NotFound.fold(model, { message, shared }),
	});

// UPDATE

const signOut = (model: Model): Model =>
	evo(model, {
		auth: () => Auth.SignedOut(),
		logInteraction: () => LogInteraction.init(),
		sidebar: Sidebar.reset,
		sessionGeneration: (generation) => generation + 1,
		page: () => Option.none(),
		balance: () => AsyncData.Idle(),
		features: () => AsyncData.Idle(),
		billingPreviewEnabled: () => false,
		a2aContextFlag: () => false,
		limitNoticeOpen: () => false,
		tags: () => AsyncData.Idle(),
		attributeSchema: () => AsyncData.Idle(),
		activityMonth: () => "",
		activity: () => AsyncData.Idle(),
		timeTravelOpen: () => false,
	});

const navigateTo = (route: Route): Update.Commands<Message, Api> => [
	Navigate({ url: toHref(route) }),
];

const foldThemeToggle: Step = (model) => {
	const theme = toggle(model.theme);
	return {
		model: evo(model, { theme: () => theme }),
		commands: [ApplyTheme({ theme })],
	};
};

const foldBalanceRefresh: Step = (model) =>
	model.auth._tag === "SignedIn"
		? {
				model: evo(model, { balance: start }),
				commands: [FetchBalance({ generation: model.sessionGeneration })],
			}
		: { model };

export const makeUpdate = (
	IdentifyAnalytics: IdentifyAnalytics,
	SignOut: SignOut,
) => {
	const slots = makeSlots(SignOut);
	const syncPage = (maybePreviousRoute: Option.Option<Route>): Step =>
		syncPageWith(slots, maybePreviousRoute);
	const foldInteraction = Update.foldChild({
		update: (
			child: LogInteraction.Model,
			input: { message: LogInteraction.Message; shared: Shared },
		) => LogInteraction.update(child, input.message, input.shared),
		read: (model: Model) => Option.some(model.logInteraction),
		write: (model: Model, logInteraction: LogInteraction.Model): Model => ({
			...model,
			logInteraction,
		}),
		toParentMessage: (message: LogInteraction.Message) =>
			Message.GotLogInteractionMessage({ message }),
		foldOutMessage:
			(out: LogInteraction.OutMessage): Step =>
			(model) => {
				const shared = toShared(model);
				if (Option.isNone(shared)) return { model };
				const refresh: Step = (model) =>
					Option.match(model.page, {
						onNone: () => ({ model }),
						onSome: (page) =>
							page._tag === "Record"
								? slots.Record.fold(model, {
										message: RecordMessage.InteractionLogged({
											nodeId: out.nodeId,
										}),
										shared: shared.value,
									})
								: page._tag === "Home" || page._tag === "Records"
									? slots[page._tag].fold(model, {
											message: SearchedMessage.SearchDebounced({
												text: page.model.searchText,
											}),
											shared: shared.value,
										})
									: { model },
					});
				return Update.combine(model, [
					refresh,
					foldTagsChanged,
					foldBalanceRefresh,
				]);
			},
	});
	const foldSidebar = Update.foldChild({
		update: (
			child: Sidebar.Model,
			input: { message: Sidebar.Message; shared: Shared },
		) => Sidebar.update(child, input.message, input.shared),
		read: (model: Model) => Option.some(model.sidebar),
		write: (model: Model, sidebar: Sidebar.Model): Model => ({
			...model,
			sidebar,
		}),
		toParentMessage: (message: Sidebar.Message) =>
			Message.GotSidebarMessage({ message }),
	});
	/**
	 * The sidebar's folder tree and waiting badge, while the Agents areas exist.
	 * ponytail: refetched on each change of area (two small GETs); poll or push if the badge must move in place.
	 */
	const refreshSidebar: Step = (model) =>
		model.auth._tag === "SignedIn" && a2aContextEnabled(model)
			? (({ model: sidebar, commands }) => ({
					model: evo(model, { sidebar: () => sidebar }),
					commands: Command.mapMessages(commands ?? [], (message) =>
						Message.GotSidebarMessage({ message }),
					),
				}))(Sidebar.refresh(model.sidebar, model.sessionGeneration))
			: { model };
	return (model: Model, message: Message): Return =>
		Message.match<Return>(message, {
			GotSidebarMessage: ({ message }) =>
				Option.match(toShared(model), {
					onNone: () => ({ model }),
					onSome: (shared) => foldSidebar(model, { message, shared }),
				}),
			ClickedLogInteraction: () => {
				const shared = toShared(model);
				if (Option.isNone(shared) || Option.isSome(shared.value.asOf))
					return { model };
				const person = Option.flatMap(model.page, (page) =>
					page._tag === "Record"
						? Option.map(AsyncData.getData(page.model.record), (record) => ({
								id: record.id,
								name: record.name,
							}))
						: Option.none<{ id: string; name: string }>(),
				);
				return foldInteraction(model, {
					message: LogInteraction.Message.Opened({ person }),
					shared: shared.value,
				});
			},
			GotLogInteractionMessage: ({ message }) => {
				const shared = toShared(model);
				return Option.isSome(shared)
					? Update.combine(model, [
							(model) =>
								foldInteraction(model, { message, shared: shared.value }),
							noteLimitHit(message),
						])
					: { model };
			},
			ClerkReady: () => ({
				model: evo(model, {
					clerkReady: () => true,
					clerkEpoch: (epoch) => epoch + 1,
				}),
			}),
			ClerkReleased: () => ({
				model: evo(model, { clerkReady: () => false }),
			}),
			FailedClerkLoad: () => ({ model: signOut(model) }),
			SessionChanged: ({ session }) => {
				if (session === null) {
					return {
						model: model.auth._tag === "SignedOut" ? model : signOut(model),
					};
				}
				if (
					model.auth._tag === "SignedIn" &&
					model.auth.user.id === session.id &&
					model.auth.user.organizationId === session.organizationId
				) {
					return Equal.equals(model.auth.user, session)
						? { model }
						: syncPage(Option.none())(
								evo(model, { auth: () => Auth.SignedIn({ user: session }) }),
							);
				}
				const sessionModel =
					model.auth._tag === "SignedIn" ? signOut(model) : model;
				const generation = sessionModel.sessionGeneration;
				return Update.combine(sessionModel, [
					(m) => ({
						model: evo(m, {
							auth: () => Auth.SignedIn({ user: session }),
							balance: start,
							features: start,
							tags: start,
							attributeSchema: start,
						}),
						commands: [
							FetchBalance({ generation }),
							FetchFeatures({ generation }),
							FetchTags({ generation }),
							FetchAttributeSchema({ entityType: null, generation }),
							prefetchActivity(m, generation),
							IdentifyAnalytics({
								id: session.id,
								email: session.email,
								createdAt: session.createdAt,
							}),
						],
					}),
					syncPage(Option.none()),
				]);
			},
			MountedClerkUi: () => ({ model }),
			ClickedLink: ({ request }) =>
				Navigation.UrlRequest.match<Return>(request, {
					Internal: ({ url }) => ({
						model,
						commands: [Navigate({ url: Url.toString(url) })],
					}),
					External: ({ href }) => ({
						model,
						commands: [LoadExternal({ href })],
					}),
				}),
			ChangedUrl: ({ url }) => {
				const route = urlToRoute(url);
				return Update.combine(
					evo(model, {
						route: () => route,
						sidebar: (sidebar) => ({ ...sidebar, drawerOpen: false }),
					}),
					[
						syncPage(Option.some(model.route)),
						// Only on a new area: Home/Records rewrite `q` on every search pause.
						...(route._tag === model.route._tag ? [] : [refreshSidebar]),
					],
				);
			},
			CompletedNavigate: () => ({ model }),
			CompletedFetchBalance: ({ generation, result }) =>
				generation === model.sessionGeneration && model.auth._tag === "SignedIn"
					? syncPage(Option.none())(
							evo(model, { balance: (balance) => settle(balance, result) }),
						)
					: { model },
			BillingPreviewChanged: ({ generation, enabled }) =>
				generation === model.sessionGeneration &&
				model.auth._tag === "SignedIn" &&
				model.billingPreviewEnabled !== enabled
					? syncPage(Option.none())(
							evo(model, { billingPreviewEnabled: () => enabled }),
						)
					: { model },
			A2aContextChanged: ({ generation, enabled }) =>
				generation === model.sessionGeneration &&
				model.auth._tag === "SignedIn" &&
				model.a2aContextFlag !== enabled
					? Update.combine(evo(model, { a2aContextFlag: () => enabled }), [
							syncPage(Option.none()),
							refreshSidebar,
						])
					: { model },
			ClickedDismissLimitNotice: () => ({
				model: evo(model, { limitNoticeOpen: () => false }),
			}),
			// The features response is the server half of the a2a-context gate, so re-gate the page.
			CompletedFetchFeatures: ({ generation, result }) =>
				generation === model.sessionGeneration && model.auth._tag === "SignedIn"
					? Update.combine(
							evo(model, { features: (features) => settle(features, result) }),
							[syncPage(Option.none()), refreshSidebar],
						)
					: { model },
			CompletedFetchTags: ({ generation, result }) => ({
				model:
					generation === model.sessionGeneration &&
					model.auth._tag === "SignedIn"
						? evo(model, { tags: (tags) => settle(tags, result) })
						: model,
			}),
			CompletedFetchAttributeSchema: ({ generation, result }) => ({
				model:
					generation === model.sessionGeneration &&
					model.auth._tag === "SignedIn"
						? evo(model, {
								attributeSchema: (schema) => settle(schema, result),
							})
						: model,
			}),
			BalanceTick: () => foldBalanceRefresh(model),
			ClickedThemeToggle: () => foldThemeToggle(model),
			RequestedSystemTheme: () => foldSystemTheme(model),
			TagsChanged: () => foldTagsChanged(model),
			AccountDeleted: () => ({
				model: signOut(model),
				commands: [SignOut({ redirectUrl: "https://rhysomn.com" })],
			}),
			CompletedTrackInteraction: () => ({ model }),
			ClickedOpenNavigation: () => ({
				model: evo(model, {
					sidebar: (sidebar) => ({ ...sidebar, drawerOpen: true }),
				}),
			}),
			ClickedCloseNavigation: () => ({
				model: evo(model, {
					sidebar: (sidebar) => ({ ...sidebar, drawerOpen: false }),
				}),
			}),
			ClickedAccountMenu: () => ({ model }),
			ClickedOrganizationSwitcher: () => ({ model }),
			CompletedSignOut: () => ({ model }),
			CompletedApplyTheme: () => ({ model }),
			CompletedInitTranslations: ({ ready }) => ({
				model: evo(model, { translationsReady: () => ready }),
			}),
			CompletedIdentifyAnalytics: () => ({ model }),
			SelectedTimeTravelDate: ({ date }) => ({
				model: evo(model, { timeTravelOpen: () => false }),
				commands: navigateTo(
					withAsOf(Option.some(`${date}T23:59:59.000Z`))(model.route),
				),
			}),
			ClickedExitTimeTravel: () => ({
				model: evo(model, { timeTravelOpen: () => false }),
				commands: navigateTo(withAsOf(Option.none())(model.route)),
			}),
			ToggledTimeTravel: ({ isOpen, month }) =>
				isOpen
					? showActivityMonth(evo(model, { timeTravelOpen: () => true }), month)
					: { model: evo(model, { timeTravelOpen: () => false }) },
			ClickedTimeTravelMonth: ({ month }) => showActivityMonth(model, month),
			// An empty `activityMonth` is "not shown yet": the prefetch's month becomes the shown one.
			CompletedFetchActivity: ({ generation, month, result }) =>
				generation === model.sessionGeneration &&
				(month === model.activityMonth || model.activityMonth === "")
					? {
							model: evo(model, {
								activityMonth: () => month,
								activity: (activity) => settle(activity, result),
							}),
						}
					: { model },
			// Re-syncing lets `/integrations` appear or vanish in place (KTD7).
			ToggledIntegrations: ({ enabled }) =>
				Update.combine(model, [
					setIntegrations(enabled),
					syncPage(Option.none()),
				]),
			CompletedPersistIntegrations: () => ({ model }),
			CompletedPersistImportDismissed: () => ({ model }),
			PressedSearchShortcut: () => ({
				model: evo(model, {
					sidebar: (sidebar) => ({ ...sidebar, drawerOpen: false }),
				}),
				commands: [
					Sidebar.hasOwnSearch(model.route._tag)
						? FocusSearch()
						: OpenSearch({
								url: linkHref(
									model.route,
									Route.Records({ asOf: Option.none(), q: Option.none() }),
								),
							}),
				],
			}),
			CompletedFocusSearch: () => ({ model }),
			GotPageMessage: ({ message }) =>
				Option.match(toShared(model), {
					onNone: () => ({ model }),
					onSome: (shared) =>
						Update.combine(model, [
							(model) => foldPageMessage(slots, model, message, shared),
							offerImport,
							noteLimitHit(message),
						]),
				}),
		});
};

// INIT

export const init: Runtime.RoutingApplicationInit<Model, Message, void, Api> = (
	url,
) => {
	const theme = readStoredTheme();
	return {
		model: {
			auth: Auth.ClerkLoading(),
			logInteraction: LogInteraction.init(),
			sidebar: Sidebar.init(Sidebar.readCollapsed()),
			sessionGeneration: 0,
			clerkReady: false,
			clerkEpoch: 0,
			route: urlToRoute(url),
			page: Option.none(),
			balance: AsyncData.Idle(),
			features: AsyncData.Idle(),
			tags: AsyncData.Idle(),
			attributeSchema: AsyncData.Idle(),
			activityMonth: "",
			activity: AsyncData.Idle(),
			timeTravelOpen: false,
			integrationsEnabled: readIntegrationsEnabled(),
			billingPreviewEnabled: false,
			a2aContextFlag: false,
			limitNoticeOpen: false,
			importDismissed: readImportDismissed(),
			theme,
			translationsReady: false,
		},
		commands: [InitTranslations(), ApplyTheme({ theme })],
	};
};

// SUBSCRIPTION

/** `Some(generation)` while signed in, `None` otherwise: the 10-second balance refresh gate (KTD3). */
export const balanceTickDependencies = (model: Model) => ({
	maybeGeneration:
		model.auth._tag === "SignedIn"
			? Option.some(model.sessionGeneration)
			: Option.none<number>(),
});

export const balanceSubscriptions = Subscription.make<Model, Message>()(
	(entry) => ({
		balanceTick: entry(
			{ maybeGeneration: S.Option(S.Number) },
			{
				modelToDependencies: balanceTickDependencies,
				dependenciesToStream: ({ maybeGeneration }) =>
					Option.isSome(maybeGeneration)
						? Stream.tick("10 seconds").pipe(
								Stream.drop(1),
								Stream.map(() => Message.BalanceTick()),
							)
						: Stream.empty,
			},
		),
	}),
);

/**
 * The Searched page's debounce, lifted to the root: `Some` while the page
 * is active with non-empty text, `None` otherwise (and after the page
 * exits). Each keystroke changes the dependencies, which restarts the
 * delay, so `SearchDebounced` fires once per pause (KTD12).
 */
export const searchDebounceDependencies = (model: Model) => ({
	maybeSearch: Option.flatMap(model.page, (page) =>
		page._tag === "Home" || page._tag === "Records"
			? Option.map(searchedDependencies(page.model).maybeText, (text) => ({
					tag: page._tag,
					text,
				}))
			: Option.none<{ tag: "Home" | "Records"; text: string }>(),
	),
});

const searchDebounced = (tag: "Home" | "Records", text: string): Message => {
	const message = SearchedMessage.SearchDebounced({ text });
	return Message.GotPageMessage({
		message:
			tag === "Home"
				? PageMessage.Home({ message })
				: PageMessage.Records({ message }),
	});
};

export const searchSubscriptions = Subscription.make<Model, Message>()(
	(entry) => ({
		searchDebounce: entry(
			{
				maybeSearch: S.Option(
					S.Struct({ tag: S.Literals(["Home", "Records"]), text: S.String }),
				),
			},
			{
				modelToDependencies: searchDebounceDependencies,
				dependenciesToStream: ({ maybeSearch }) =>
					Option.match(maybeSearch, {
						onNone: () => Stream.empty,
						onSome: ({ tag, text }) =>
							Stream.fromEffect(
								Effect.sleep("300 millis").pipe(
									Effect.as(searchDebounced(tag, text)),
								),
							),
					}),
			},
		),
	}),
);

/** Cmd-K (macOS) / Ctrl-K (elsewhere) listens on every signed-in page: Home/Records focus their own search, the rest the top bar's (U16). */
export const searchShortcutDependencies = (model: Model) => ({
	isListening: Option.isSome(model.page),
});

export const toSearchShortcutMessage = (
	event: KeyboardEvent,
): Option.Option<Message> => {
	if ((event.metaKey || event.ctrlKey) && event.key === "k") {
		event.preventDefault();
		return Option.some(Message.PressedSearchShortcut());
	}
	return Option.none();
};

/** The picker holds its own open state (KTD3 keeps only `asOf` in the URL), so a
 * heatmap re-render cannot close it; these dismiss it on Escape or a click outside. */
export const TIME_TRAVEL_ROOT_ID = "time-travel-root";

const closeTimeTravel = Message.ToggledTimeTravel({ isOpen: false, month: "" });

export const toTimeTravelDismissMessage = (
	event: Event,
): Option.Option<Message> => {
	if (event instanceof KeyboardEvent) {
		return event.key === "Escape"
			? Option.some(closeTimeTravel)
			: Option.none();
	}
	const target = event.target;
	return target instanceof Element &&
		target.closest(`#${TIME_TRAVEL_ROOT_ID}`) !== null
		? Option.none()
		: Option.some(closeTimeTravel);
};

export const timeTravelSubscriptions = Subscription.make<Model, Message>()(
	(entry) => ({
		timeTravelDismiss: entry(
			{ isOpen: S.Boolean },
			{
				modelToDependencies: (model) => ({ isOpen: model.timeTravelOpen }),
				dependenciesToStream: ({ isOpen }) =>
					Stream.when(
						Stream.merge(
							Subscription.fromEventFilterMap<MouseEvent, Message>({
								target: window,
								type: "click",
								toMessage: toTimeTravelDismissMessage,
							}),
							Subscription.fromEventFilterMap<KeyboardEvent, Message>({
								target: window,
								type: "keydown",
								toMessage: toTimeTravelDismissMessage,
							}),
						),
						Effect.sync(() => isOpen),
					),
			},
		),
	}),
);

export const shortcutSubscriptions = Subscription.make<Model, Message>()(
	(entry) => ({
		searchShortcut: entry(
			{ isListening: S.Boolean },
			{
				modelToDependencies: searchShortcutDependencies,
				dependenciesToStream: ({ isListening }) =>
					Stream.when(
						Subscription.fromEventFilterMap<KeyboardEvent, Message>({
							target: window,
							type: "keydown",
							toMessage: toSearchShortcutMessage,
						}),
						Effect.sync(() => isListening),
					),
			},
		),
	}),
);

// PAGE SUBSCRIPTIONS (KTD12: gated on a page slice, lifted to the root)

const toChatMessage = (message: AiChatMessage): Message =>
	Message.GotPageMessage({
		message: PageMessage.Chat({
			message: ChatMessage.GotChatMessage({ message }),
		}),
	});

/** `Some` while the Chat page has a request pending; `None` otherwise and once the page exits. */
export const chatStreamDependencies = (model: Model) => ({
	maybeRequest: Option.flatMap(model.page, (page) =>
		page._tag === "Chat"
			? chatDependencies(page.model).maybeRequest
			: Option.none<PendingRequest>(),
	),
});

/** `/chat/stream` as a Subscription over the response body (KTD5, KTD6). */
export const chatSubscriptions = Subscription.make<Model, Message, Api>()(
	(entry) => ({
		chatStream: entry(
			{ maybeRequest: S.Option(PendingRequest) },
			{
				modelToDependencies: chatStreamDependencies,
				dependenciesToStream: ({ maybeRequest }) =>
					Option.match(maybeRequest, {
						onNone: () => Stream.empty,
						onSome: (request) =>
							streamChat(request).pipe(Stream.map(toChatMessage)),
					}),
			},
		),
	}),
);

const toAiMessage = (message: AiMessage): Message =>
	Message.GotPageMessage({ message: PageMessage.AI({ message }) });

const aiCountdownMessage = Clock.currentTimeMillis.pipe(
	Effect.map((now) => toAiMessage(AiMessage.Ticked({ now }))),
);

/** `Some(id)` while the AI page has a session requested or active. */
export const aiSessionRootDependencies = (model: Model) => ({
	maybeSessionId: Option.flatMap(model.page, (page) =>
		page._tag === "AI"
			? aiSessionDependencies(page.model).maybeSessionId
			: Option.none<string>(),
	),
});

/** `Some` once the AI session connected: the one-second countdown gate. */
export const aiCountdownRootDependencies = (model: Model) => ({
	maybeStarted: Option.flatMap(model.page, (page) =>
		page._tag === "AI"
			? aiCountdown(page.model).maybeStarted
			: Option.none<{ id: string; startedAt: number }>(),
	),
});

/** The WebRTC voice session and its countdown (v1 `AI.tsx`), torn down when the slice clears or the page exits. */
export const aiSubscriptions = Subscription.make<Model, Message, Api>()(
	(entry) => ({
		aiSession: entry(
			{ maybeSessionId: S.Option(S.String) },
			{
				modelToDependencies: aiSessionRootDependencies,
				dependenciesToStream: ({ maybeSessionId }) =>
					Option.match(maybeSessionId, {
						onNone: () => Stream.empty,
						onSome: (id) => aiSession(id).pipe(Stream.map(toAiMessage)),
					}),
			},
		),
		aiCountdown: entry(
			{
				maybeStarted: S.Option(S.Struct({ id: S.String, startedAt: S.Number })),
			},
			{
				modelToDependencies: aiCountdownRootDependencies,
				dependenciesToStream: ({ maybeStarted }) =>
					Option.isSome(maybeStarted)
						? Stream.tick("1 second").pipe(
								Stream.drop(1),
								Stream.mapEffect(() => aiCountdownMessage),
							)
						: Stream.empty,
			},
		),
	}),
);
